// The WGSL twin of the instanced quad batch (`../../gfx/quads.ts`).
//
// The picture is the GLSL pair's, line for line. What the port changes is
// what the language forces: one module holds both stages, the varyings are
// one struct both stages read, every uniform is a member of the generated
// block, and the ternary becomes `select`, whose FALSE arm comes first.
//
// The atlas read is the ONE `textureSample` of the whole client. It sits at
// the top level of `fs`, so the control flow is uniform and the call may
// take its own derivative; every plane tap of a world pass sits in a branch
// and reads with `textureSampleLevel` instead.

import type { AttribSpec } from '../../gfx/types';
import type { UniformSpec } from '../prelude';
import { cornerDecodeWgsl } from '../../gl/corner';
import { wgslModule } from './module';
import { SAMPLER_ATLAS } from './bindings';

/** The varyings the two stages of the quad batch share. */
const QUAD_VARYINGS_WGSL = `struct VsOut {
  @builtin(position) pos: vec4f,
  @location(0) vUV: vec2f,
  @location(1) vColor: vec4f,
}
`;

const QUAD_VERTEX_WGSL = `@vertex
fn vs(in: VsIn) -> VsOut {
  var out: VsOut;
${cornerDecodeWgsl()}
  let flags = i32(in.aFlags + 0.5);
  let st = (flags >> 3u) & 7;                              // F_STEP_SHIFT (R1796)
  let ks = select(select(select(select(1.0, U.uStep.w, st == 4), U.uStep.z, st == 3), U.uStep.y, st == 2), U.uStep.x, st == 1);
  let k = select(1.0, 1.0 / U.uZoom, (flags & 1) != 0) * ks;   // F_SCREEN
  let local = ((corner - 0.5) * in.aSize + in.aOffset) * k;
  let c = cos(in.aRot);
  let s = sin(in.aRot);
  let world = in.aPos + vec2f(local.x * c - local.y * s, local.x * s + local.y * c);
  out.pos = vec4f((U.uViewProj * vec3f(world, 1.0)).xy, 0.0, 1.0);
  out.vUV = mix(in.aUV.xy, in.aUV.zw, corner);
  let a = select(in.aColor.a, 0.0, (flags & 2) != 0);     // F_ADD
  out.vColor = vec4f(in.aColor.rgb * in.aColor.a, a);
  return out;
}
`;

// The atlas texel is premultiplied and so is vColor.rgb, so the product is
// the tinted premultiplied colour and the alpha is the plain product.
//
// `uAlpha` is the batch's own fade, and it scales the premultiplied output
// whole: a layer that dims everything it draws — the arrival veils the map,
// a piece of ambience fades in — writes one uniform instead of touching the
// alpha of every instance it packs. The default is 1, where the product is
// the shipping pixel.
const QUAD_FRAGMENT_WGSL = `@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  let t = textureSample(uTex, ${SAMPLER_ATLAS}, in.vUV);
  return vec4f(t.rgb * in.vColor.rgb, t.a * in.vColor.a) * U.uAlpha;
}
`;

/** The whole WGSL module of the quad batch: the header and both stages. */
export function quadModuleWgsl(
  specs: readonly UniformSpec[], attribs: readonly AttribSpec[],
): string {
  return wgslModule({
    uniforms: specs, attribs, varyings: QUAD_VARYINGS_WGSL,
    vertex: QUAD_VERTEX_WGSL, body: QUAD_FRAGMENT_WGSL,
  });
}
